import { useState, type FormEvent } from 'react';

const newsletterSubmitAddr = 'https://readdy.ai/api/form/d9a9da4nib0mhnm9k2ag';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const currentYear = new Date().getFullYear();

  async function handleNewsletterSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus('submitting');
    setStatusMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setStatus('success');
      setStatusMessage('Thank you for subscribing!');
      setEmail('');
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch(newsletterSubmitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        setStatusMessage('Thank you for subscribing! You will receive weekly Superbuy Spreadsheet updates.');
        setEmail('');
        form.reset();
      } else if (serverMsg && (typeof serverMsg === 'string') && serverMsg.toLowerCase().includes('spam')) {
        setStatus('error');
        setStatusMessage('Unable to process your subscription at this time. Please try again later.');
      } else {
        setStatus('error');
        setStatusMessage(typeof serverMsg === 'string' ? serverMsg : 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setStatusMessage('Network error. Please check your connection and try again.');
    }
  }

  return (
    <footer className="bg-secondary-900 pt-16 pb-8 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        {/* Top grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-14">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-full bg-secondary-500 flex items-center justify-center">
                <i className="ri-store-2-line text-background-50 text-base"></i>
              </div>
              <span className="font-heading text-lg font-semibold text-background-50">SuperBuySpread</span>
            </div>
            <p className="text-sm text-secondary-200 leading-relaxed">
              Your ultimate fashion product discovery platform. Curated categories, updated weekly, designed to help you find exactly what you are looking for.
            </p>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-xs font-semibold tracking-widest uppercase text-secondary-300 mb-4">Newsletter</h4>
            <form
              data-readdy-form
              id="newsletter-footer"
              onSubmit={handleNewsletterSubmit}
              className="flex flex-col gap-3"
            >
              {/* Honeypot */}
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="spam-guard"
              />
              <div className="relative">
                <input
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full bg-transparent border-0 border-b border-secondary-600 pb-2 text-sm text-background-50 placeholder-secondary-400 focus:outline-none focus:border-background-50 transition-colors"
                />
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="absolute right-0 top-0 text-secondary-300 hover:text-background-50 transition-colors cursor-pointer disabled:opacity-50"
                >
                  <i className="ri-arrow-right-line text-lg"></i>
                </button>
              </div>
              {statusMessage && (
                <p className={`text-xs ${status === 'success' ? 'text-secondary-300' : 'text-accent-300'}`}>
                  {statusMessage}
                </p>
              )}
            </form>
            <p className="text-xs text-secondary-400 mt-3">
              <a href="/superbuy-spreadsheet-guide" className="hover:text-secondary-200 transition-colors">
                Get weekly Superbuy Spreadsheet updates
              </a>
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-xs font-semibold tracking-widest uppercase text-secondary-300 mb-4">Shop</h4>
            <div className="flex flex-col gap-2">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-secondary-200 hover:text-background-50 transition-colors whitespace-nowrap">Superbuy Sheet</a>
              <a href="https://kaspreadsheet.com/category-911-Shoes.html" target="_blank" rel="noopener nofollow" className="text-sm text-secondary-200 hover:text-background-50 transition-colors whitespace-nowrap">Shoes</a>
              <a href="https://kaspreadsheet.com/search-912/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-sm text-secondary-200 hover:text-background-50 transition-colors whitespace-nowrap">Hoodies/Sweaters</a>
              <a href="https://kaspreadsheet.com/search-920/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-sm text-secondary-200 hover:text-background-50 transition-colors whitespace-nowrap">Accessories</a>
            </div>
          </div>

          {/* More links */}
          <div>
            <h4 className="text-xs font-semibold tracking-widest uppercase text-secondary-300 mb-4">Resources</h4>
            <div className="flex flex-col gap-2">
              <a href="/superbuy-spreadsheet-guide" className="text-sm text-secondary-200 hover:text-background-50 transition-colors whitespace-nowrap">Complete Guide</a>
              <a href="/superbuy-spreadsheet-shoes-guide" className="text-sm text-secondary-200 hover:text-background-50 transition-colors whitespace-nowrap">Shoes Guide</a>
              <a href="/comparison" className="text-sm text-secondary-200 hover:text-background-50 transition-colors whitespace-nowrap">Comparison</a>
              <a href="/is-superbuy-spreadsheet-safe" className="text-sm text-secondary-200 hover:text-background-50 transition-colors whitespace-nowrap">Safety Guide</a>
              <a href="/" className="text-sm text-secondary-200 hover:text-background-50 transition-colors whitespace-nowrap">Home</a>
              <a href="#categories" className="text-sm text-secondary-200 hover:text-background-50 transition-colors whitespace-nowrap">All Categories</a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-secondary-200 hover:text-background-50 transition-colors whitespace-nowrap">Main Website</a>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-secondary-700 pt-8">
          {/* Large brand letters */}
          <div className="text-center mb-6 overflow-hidden">
            <p className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-secondary-800 tracking-tight whitespace-nowrap select-none">
              SUPERBUYSPREAD
            </p>
          </div>

          {/* Bottom bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-secondary-500">
              &copy; {currentYear} SuperBuySpread. All rights reserved.
            </p>
            <p className="text-xs text-secondary-500">
              Updated weekly &bull; Research-based guides &bull; Community focused
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}